import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Button, toast, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AssemblyVersionBoxClient, formatNumber, refusalMessage, type AssemblyVersionBoxDTO, type AssemblyVersionDTO } from "../../api/manufacturing";

/**
 * A version's cartons (design §14): each box's own SKU and UPC (what the 3PL
 * labels and reports), its size and weight. Boxes stay numbered 1..n, as the
 * assembly BOM lines and the packing list number them: one is added at the
 * end, only the last one goes, and only when nothing is in it. They are not
 * the product's packages (Measurements tab), which nexpim keeps as they are.
 */
export function VersionBoxes({ version, readOnly }: { version: AssemblyVersionDTO; readOnly: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const client = useMemo(() => new AssemblyVersionBoxClient(http), [http]);
  const key = ["manufacturing", "assembly-version-box", version.id];
  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: async () => (await client.find({ size: 100, filters: { assembly_version_id: [String(version.id)] } })).content,
  });
  const boxes = data ?? [];
  const last = boxes.reduce((max, box) => Math.max(max, box.box_number), 0);
  // Box numbers the version uses (its assembly BOM's lines) that have no record:
  // the go-live seed found no package for them. Adding fills those first.
  const used = Math.max(version.box_count ?? 1, last);
  const missing = Array.from({ length: used }, (_, index) => index + 1).filter((number) => !boxes.some((box) => box.box_number === number));
  const next = missing[0] ?? last + 1;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["manufacturing"] });
  const fail = (failure: unknown) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));

  const add = useMutation({
    mutationFn: () =>
      client.create({
        id: 0,
        assembly_version_id: version.id,
        box_number: next,
        package_sku: null,
        package_upc: null,
        width_in_inches: 0,
        height_in_inches: 0,
        depth_in_inches: 0,
        weight_in_lbs: 0,
      }),
    onSuccess: refresh,
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (box: AssemblyVersionBoxDTO) => client.remove(box.id),
    onSuccess: refresh,
    onError: fail,
  });

  const columns = useMemo(() => {
    const c = defineColumns<AssemblyVersionBoxDTO>();
    return [
      c.number("box_number", (row) => row.box_number, { header: t("assembly.box.number", "Box"), priority: 1 }),
      c.text("package_sku", (row) => row.package_sku ?? "", { header: t("assembly.box.sku", "Box SKU"), priority: 1 }),
      c.text("package_upc", (row) => row.package_upc ?? "", { header: t("assembly.box.upc", "Box UPC"), priority: 2 }),
      c.text("size", (row) => `${formatNumber(row.width_in_inches)} × ${formatNumber(row.height_in_inches)} × ${formatNumber(row.depth_in_inches)} in`, {
        header: t("assembly.box.size", "W × H × D"),
        priority: 2,
      }),
      c.number("width_in_inches", (row) => Number(row.width_in_inches), { header: t("assembly.box.width", "Width (in)"), defaultVisible: false }),
      c.number("height_in_inches", (row) => Number(row.height_in_inches), { header: t("assembly.box.height", "Height (in)"), defaultVisible: false }),
      c.number("depth_in_inches", (row) => Number(row.depth_in_inches), { header: t("assembly.box.depth", "Depth (in)"), defaultVisible: false }),
      c.number("weight_in_lbs", (row) => Number(row.weight_in_lbs), { header: t("assembly.box.weight", "Weight (lb)"), priority: 2 }),
    ];
  }, [t]);

  const number = (value: string) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {!isLoading && missing.length > 0 && (
        <p className="shrink-0 border-b border-line bg-warn-soft px-4 py-1.5 text-[12px] lg:px-6">
          {t("assembly.box.missing", "The assembly BOM puts lines in box {{list}}, which has no record here (no package said so): add it and give it its SKU, UPC and size.", {
            list: missing.join(", "),
            count: missing.length,
          })}
        </p>
      )}
      <DataTable<AssemblyVersionBoxDTO>
        data={boxes}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => String(row.id)}
        hidePrint
        toolbarLead={
          <span className="text-[12px] text-ink-muted">
            {t("assembly.box.own.v2", "The cartons of this version, as its assembly BOM numbers them. The product's packages (Measurements tab) are separate.")}
          </span>
        }
        toolbarExtra={
          !readOnly ? (
            <Button size="sm" variant="secondary" icon={<Plus className="size-4" />} loading={add.isPending} onClick={() => add.mutate()}>
              {missing.length > 0 ? t("assembly.box.add-missing", "Add box {{n}}", { n: next }) : t("assembly.box.add", "Add a box")}
            </Button>
          ) : undefined
        }
        editor={
          readOnly
            ? undefined
            : {
                fields: {
                  package_sku: { kind: "text", get: (row) => row.package_sku ?? "", set: (row, value) => ({ ...row, package_sku: value || null }) },
                  package_upc: { kind: "text", get: (row) => row.package_upc ?? "", set: (row, value) => ({ ...row, package_upc: value || null }) },
                  width_in_inches: { kind: "number", get: (row) => row.width_in_inches, set: (row, value) => ({ ...row, width_in_inches: number(value) }) },
                  height_in_inches: { kind: "number", get: (row) => row.height_in_inches, set: (row, value) => ({ ...row, height_in_inches: number(value) }) },
                  depth_in_inches: { kind: "number", get: (row) => row.depth_in_inches, set: (row, value) => ({ ...row, depth_in_inches: number(value) }) },
                  weight_in_lbs: { kind: "number", get: (row) => row.weight_in_lbs, set: (row, value) => ({ ...row, weight_in_lbs: number(value) }) },
                },
                onSave: async (row) => {
                  try {
                    await client.update(row);
                    await refresh();
                  } catch (failure) {
                    throw new Error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
                  }
                },
              }
        }
        rowActions={(row) =>
          !readOnly && row.box_number === last && last > 1 ? (
            <Button
              size="sm"
              variant="ghost"
              aria-label={t("assembly.box.remove", "Remove the last box")}
              icon={<Trash2 className="size-4" />}
              onClick={() => {
                void (async () => {
                  const ok = await confirm({
                    title: t("assembly.box.remove.title", "Remove box {{n}}?", { n: row.box_number }),
                    message: t("assembly.box.remove.message.v2", "Only an empty box goes: no line of the assembly BOM and no piece in it."),
                    confirmLabel: t("assembly.box.remove.confirm", "Remove"),
                    variant: "danger",
                  });
                  if (ok) remove.mutate(row);
                })();
              }}
            />
          ) : null
        }
        emptyMessage={t("assembly.box.empty", "No box yet: one box is assumed. Add the cartons it ships in.")}
      />
    </div>
  );
}
